<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const apps = computed(() => store.getters['appBuilder/getApps']);
const uiFlags = computed(() => store.getters['appBuilder/getUIFlags']);

const createDialogRef = ref(null);
const newAppName = ref('');
const newAppSlug = ref('');
// True when the apps endpoint is unreachable for this account (404/403) — the App
// Builder feature is not enabled on the backend account, so we show a clear notice
// instead of the "create your first app" state (whose create button would also fail).
const loadError = ref(false);

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function onNameInput() {
  newAppSlug.value = slugify(newAppName.value);
}

function openCreateDialog() {
  newAppName.value = '';
  newAppSlug.value = '';
  createDialogRef.value?.open();
}

async function confirmCreate() {
  if (!newAppName.value.trim()) return;
  try {
    const app = await store.dispatch('appBuilder/createApp', {
      name: newAppName.value.trim(),
      slug: newAppSlug.value || slugify(newAppName.value),
    });
    createDialogRef.value?.close();
    useAlert(t('APP_BUILDER.ALERTS.CREATED'));
    router.push({ name: 'app_editor', params: { appId: app.id } });
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

function openEditor(app) {
  router.push({ name: 'app_editor', params: { appId: app.id } });
}

async function duplicateApp(app) {
  try {
    const copy = await store.dispatch('appBuilder/createApp', {
      name: t('APP_BUILDER.DUPLICATE_NAME', { name: app.name }),
      slug: `${app.slug}-copy-${Date.now().toString(36)}`,
      page_title: app.page_title,
      header_text: app.header_text,
      color: app.color,
      config: app.config,
    });
    useAlert(t('APP_BUILDER.ALERTS.DUPLICATED'));
    router.push({ name: 'app_editor', params: { appId: copy.id } });
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

// Publish/unpublish is modeled by the `archived` flag: published => visible.
async function togglePublish(app) {
  try {
    await store.dispatch('appBuilder/updateApp', {
      id: app.id,
      archived: !app.archived,
    });
    useAlert(
      app.archived
        ? t('APP_BUILDER.ALERTS.PUBLISHED')
        : t('APP_BUILDER.ALERTS.UNPUBLISHED')
    );
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

onMounted(async () => {
  try {
    await store.dispatch('appBuilder/fetchApps');
  } catch (error) {
    const status = error?.response?.status;
    loadError.value = status === 404 || status === 403;
    if (!loadError.value) useAlert(t('APP_BUILDER.ALERTS.LOAD_ERROR'));
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('APP_BUILDER.HEADER_TITLE') }}
      </h1>
      <NextButton
        v-if="!loadError"
        :label="t('APP_BUILDER.CREATE')"
        icon="i-lucide-plus"
        color="blue"
        size="sm"
        @click="openCreateDialog"
      />
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <p v-if="uiFlags.isFetching" class="text-n-slate-11">
        {{ t('APP_BUILDER.LOADING') }}
      </p>

      <EmptyStateLayout
        v-else-if="loadError"
        :title="t('APP_BUILDER.NOT_ENABLED.TITLE')"
        :subtitle="t('APP_BUILDER.NOT_ENABLED.SUBTITLE')"
        :show-backdrop="false"
      />

      <EmptyStateLayout
        v-else-if="!apps.length"
        :title="t('APP_BUILDER.EMPTY_STATE.TITLE')"
        :subtitle="t('APP_BUILDER.EMPTY_STATE.SUBTITLE')"
        :show-backdrop="false"
      >
        <template #actions>
          <NextButton
            :label="t('APP_BUILDER.CREATE')"
            icon="i-lucide-plus"
            color="blue"
            @click="openCreateDialog"
          />
        </template>
      </EmptyStateLayout>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <CardLayout v-for="app in apps" :key="app.id">
          <div class="flex items-start justify-between gap-2">
            <button
              class="flex flex-col items-start gap-1 min-w-0 text-left"
              @click="openEditor(app)"
            >
              <span class="text-base font-medium text-n-slate-12 truncate">
                {{ app.name }}
              </span>
              <span class="text-xs text-n-slate-10 truncate">
                {{ `/p/${app.slug}` }}
              </span>
            </button>
            <span
              class="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
              :class="
                app.archived
                  ? 'text-n-slate-10 bg-n-alpha-2'
                  : 'text-emerald-500 bg-emerald-500/10'
              "
            >
              {{
                app.archived
                  ? t('APP_BUILDER.STATUS.DRAFT')
                  : t('APP_BUILDER.STATUS.PUBLISHED')
              }}
            </span>
          </div>
          <div class="flex items-center gap-2 pt-2">
            <NextButton
              :label="t('APP_BUILDER.ACTIONS.EDIT')"
              variant="faded"
              color="slate"
              size="xs"
              @click="openEditor(app)"
            />
            <NextButton
              :label="t('APP_BUILDER.ACTIONS.DUPLICATE')"
              variant="ghost"
              color="slate"
              size="xs"
              @click="duplicateApp(app)"
            />
            <NextButton
              :label="
                app.archived
                  ? t('APP_BUILDER.ACTIONS.PUBLISH')
                  : t('APP_BUILDER.ACTIONS.UNPUBLISH')
              "
              variant="ghost"
              :color="app.archived ? 'teal' : 'amber'"
              size="xs"
              @click="togglePublish(app)"
            />
          </div>
        </CardLayout>
      </div>
    </div>

    <!-- Create dialog -->
    <Dialog
      ref="createDialogRef"
      type="edit"
      :title="t('APP_BUILDER.CREATE_DIALOG.TITLE')"
      :description="t('APP_BUILDER.CREATE_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('APP_BUILDER.CREATE_DIALOG.CONFIRM')"
      :disable-confirm-button="!newAppName.trim()"
      :is-loading="uiFlags.isCreating"
      @confirm="confirmCreate"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="newAppName"
          :label="t('APP_BUILDER.CREATE_DIALOG.NAME_LABEL')"
          :placeholder="t('APP_BUILDER.CREATE_DIALOG.NAME_PLACEHOLDER')"
          autofocus
          @input="onNameInput"
        />
        <Input
          v-model="newAppSlug"
          :label="t('APP_BUILDER.CREATE_DIALOG.SLUG_LABEL')"
          :placeholder="t('APP_BUILDER.CREATE_DIALOG.SLUG_PLACEHOLDER')"
          :message="t('APP_BUILDER.CREATE_DIALOG.SLUG_HINT')"
        />
      </div>
    </Dialog>
  </div>
</template>
